/**
 * useTaskGeoJson Hook
 *
 * Converts an array of Task objects into a GeoJSON FeatureCollection
 * suitable for MapLibre's GeoJSON source with clustering.
 *
 * Semantics: ONE Point feature per task, anchored at `task.anchorPoint`,
 * regardless of which geometry types the task contains. Tasks without a
 * Marker geometry contribute to cluster counts but are filtered out of the
 * unclustered marker render layers via the `hasMarker` flag (Line/Shape
 * geometries are drawn separately by `TaskGeometryOverlays`).
 *
 * Color resolution replicates the priority chain from useSharedMarkerStyle:
 * 1. Custom marker color (first marker geometry's `markerColor`)
 * 2. Status-based color (concrete hex, not CSS variables)
 * 3. Default fallback (#6B7280)
 *
 * MapLibre WebGL cannot resolve CSS variables, so all colors are concrete hex values.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/hooks/useTaskGeoJson
 */

import { useMemo } from 'react';

import type { Feature, FeatureCollection, Point } from 'geojson';

import {
  STATUS_HEX_COLORS,
  DEFAULT_MARKER_HEX_FALLBACK,
  DEFAULT_MARKER_SHAPE,
  DEFAULT_MARKER_ICON,
} from '@/constants/geometryRendering';
import {
  MAP_CLUSTER_FEATURE_KIND_TASK,
  type MapClusterFeatureKind,
} from '@/components/map/MapClusterLayer/featureKind';
import type { Task } from '@/types';
import { GeometryType } from '@/types/geometry';
import type { TaskGeometry } from '@/types/geometry';

// ===== Types =====

interface UseTaskGeoJsonProps {
  /** Array of tasks to convert to GeoJSON features */
  tasks: Task[];
  /** Task ID currently being edited (excluded from output) */
  editingTaskId: string | null;
}

export interface TaskFeatureProperties {
  /** Discriminator for the unified cluster source. */
  featureKind: MapClusterFeatureKind;
  /** Task unique identifier (GUID) */
  id: string;
  /** URL-friendly short identifier (NanoID) */
  shortId: string;
  /** Resolved hex color for symbol rendering */
  color: string;
  /** Marker shape value: 'Circle' | 'Square' | 'Triangle' */
  shape: string;
  /** Icon key or empty string if no icon */
  icon: string;
  /** Task status for cluster aggregation */
  status: string;
  /** Task title for potential tooltips */
  title: string;
  /**
   * `true` when the task has at least one Marker geometry — drives the
   * unclustered marker render filter. Tasks with only Line/Shape geometries
   * still contribute to cluster counts but render no marker glyph.
   */
  hasMarker: boolean;
}

// ===== Color Resolution =====

/**
 * Resolves the display color for a task following the priority chain:
 * 1. Custom marker color from the provided marker geometry
 * 2. Status-based hex color
 * 3. Default fallback
 */
function resolveTaskColor(
  task: Task,
  markerGeometry: TaskGeometry | undefined
): string {
  if (markerGeometry?.markerColor) {
    return markerGeometry.markerColor;
  }

  // Status-based color (concrete hex values for WebGL)
  const statusColor = STATUS_HEX_COLORS[task.status];
  if (statusColor) {
    return statusColor;
  }

  return DEFAULT_MARKER_HEX_FALLBACK;
}

// ===== GeoJSON Conversion =====

/**
 * Converts a single task to exactly one GeoJSON Point feature.
 *
 * Position: the first Marker geometry's `calculatedCenter` when present
 * (preserves the previous "marker drawn at its own position" behavior for
 * single-marker tasks); `task.anchorPoint` otherwise. Style properties
 * come from the first Marker geometry; tasks without any Marker geometry
 * fall back to defaults and `hasMarker` is `false` so the marker render
 * filter skips them — they still inflate the cluster count.
 */
function taskToFeature(task: Task): Feature<Point, TaskFeatureProperties> {
  const firstMarker = task.geometries?.find(
    (geometry) => geometry.type === GeometryType.Marker
  );
  const hasMarker = firstMarker !== undefined;
  const position = firstMarker?.calculatedCenter ?? task.anchorPoint;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [position.lng, position.lat],
    },
    properties: {
      featureKind: MAP_CLUSTER_FEATURE_KIND_TASK,
      id: task.id,
      shortId: task.shortId,
      color: resolveTaskColor(task, firstMarker),
      shape: firstMarker?.markerShape ?? DEFAULT_MARKER_SHAPE,
      icon: firstMarker?.markerIcon ?? DEFAULT_MARKER_ICON,
      status: task.status,
      title: task.title,
      hasMarker,
    },
  };
}

// ===== Hook =====

/**
 * Converts tasks to a memoized GeoJSON FeatureCollection for MapLibre Symbol Layer.
 *
 * Features:
 * - Excludes the currently-editing task (rendered by CurrentGeometriesRenderer as DOM)
 * - Resolves colors to concrete hex values (MapLibre WebGL cannot use CSS variables)
 * - Signature-based memoization prevents unnecessary recalculations
 *
 * @param props - Tasks array and optional editing task ID
 * @returns GeoJSON FeatureCollection of Point features with symbol properties
 */
export function useTaskGeoJson({
  tasks,
  editingTaskId,
}: UseTaskGeoJsonProps): FeatureCollection<Point, TaskFeatureProperties> {
  // Signature-based key prevents recalculation when task array reference changes
  // but content remains the same
  const tasksSignature = useMemo(
    () =>
      tasks
        .map((task) => `${task.id}:${task.updatedAt?.getTime() ?? 0}`)
        .join(','),
    [tasks]
  );

  return useMemo(() => {
    const visibleTasks = editingTaskId
      ? tasks.filter((task) => task.id !== editingTaskId)
      : tasks;

    return {
      type: 'FeatureCollection',
      features: visibleTasks.map(taskToFeature),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksSignature, editingTaskId]);
}
